// Copyright (c) Jonathan Ferraz.
// Licensed under the MIT license.

import { down } from 'assets/styles/breakpoint';
import { theme } from 'assets/styles/theme';
import styled, { css } from 'styled-components';

interface ButtonProps {
  border?: boolean;
  color?: string;
}

export const PageContainer = styled.div`
  padding-top: ${theme.headerHeight};
  width: 100%;
  height: fit-content;
  position: relative;
`;

export const ButtonWrapper = styled.div`
  padding-top: 30px;
  display: flex;
  gap: 20px;

  @media (max-width: 768px) {
    padding-top: 20px;
    gap: 10px;
  }
`;

export const StyledButton = styled.button<ButtonProps>`
  /* color: ${(props) => props.color}; */
  color: #000;
  font-size: 16px;
  border-radius: 10px;
  font-weight: 600;
  /* border: 2px solid ${(props) => props.color}; */
  border: none;
  background-color: ${(props) => props.color};
  padding: 8px 18px;
  transition: 0.3s ease-in-out;

  ${(props) =>
    props.border &&
    css`
      border: 2px solid ${(props: any) => props.color};
      color: ${(props: any) => props.color};
      background-color: transparent;
    `}

  &:hover {
    filter: brightness(0.75);

    ${(props) =>
      props.border &&
      css`
        background-color: ${(props: any) => props.color};
        color: #000;
      `}
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 3px 11px;
    border-radius: 8px;
  }
`;

export const TextWrapper = styled.div`
  width: 100%;
  height: 600px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  max-width: 1100px;
  margin: 0 auto;

  svg {
    transform: scale(1.4);
    margin-left: 14px;
  }

  p {
    padding-top: 20px;
    color: #fff;
    font-size: 24px;
    font-family: 'SF Pro Display';
    font-weight: 300;
    width: 360px;

    strong {
      font-weight: 300;
      background-image: linear-gradient(
        90deg,
        rgba(171, 168, 236, 1),
        rgba(184, 198, 229, 1)
      );
      background-size: 100% 300%;
      background-position: 50% 0;
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      user-select: none;
    }
  }

  @media (max-width: 768px) {
    height: 35vh;

    svg {
      transform: scale(0.9);
      margin-left: -6px;
    }

    p {
      font-size: 15px;
      font-style: normal;
      font-weight: 400;
      padding-top: 10px;
      max-width: 230px;
    }
  }
`;

export const ImgContainer = styled.div`
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  position: absolute;
  user-select: none;
  pointer-events: none;
  position: none;
  z-index: 1;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    margin-left: 120px;
    margin-top: -120px;
    width: 100%;
    max-width: 1500px;
    min-width: 1500px;
    z-index: 1;
  }

  svg {
    position: absolute;
    top: 300px;
    margin: 0 auto;
    margin-left: -60px;
    transform: scale(1.2);
  }

  @media (max-width: 1000px) {
    svg {
      display: none;
    }

    /* img {
      margin-top: -100px;
      max-width: 1200px;
      min-width: 1200px;
    } */
  }

  @media (max-width: 768px) {
    max-height: 50vh;
    z-index: 1;

    svg {
      z-index: 2;
      top: 120px;
      right: 70px;
      margin: unset;
      max-width: 40px;

      background: #000;
      height: auto;
      border-radius: 100%;
    }

    div {
      position: relative;
      height: 100%;
      width: 100%;

      img {
        position: absolute;
        width: 110%;
        min-width: unset;
        left: -14px;
        top: 130px;
        margin-left: unset;
        margin-top: unset;
      }
    }
  }
`;

export const CardsWrapper = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1250px;
  margin: 0 auto;
  padding: 0 20px;

  > img {
    z-index: -1;
    position: absolute;
    top: 20vh;
    left: -5vh;
    width: 100%;
  }

  @media (max-width: 1000px) {
    > img {
      top: 5vh;
      left: -15vh;
      width: 150%;
    }
  }

  ${down.md`
    display: flex;
    justify-content: center;
    align-items: center;
  `};

  @media (max-width: 768px) {
    padding: 0px;

    > img {
      z-index: -1;
      position: absolute;
      top: 18vh;
      left: -2vh;
      width: 110%;
    }
  }
`;

export const WrapperCards = styled.div<{ left?: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${({ left }) => (left ? 'flex-start' : 'center')};
  margin-left: ${({ left }) => left && '36px'};
`;

export const ProgressBar = styled.div<{ color?: string; size: number }>`
  background-color: ${({ color }) =>
    color ? color : `rgba(255, 255, 255, 0.24)`};
  border-radius: 50px;
  width: ${({ size }) => size}%;
  height: 10px;
  margin: ${({ color }) => color && '16px 0px 8px 0px'};
`;

export const Grid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 0.5fr);
  grid-column-gap: 20px;
  grid-row-gap: 20px;
  padding-top: 120px;
  padding-bottom: 150px;

  .div1 {
    grid-area: 1 / 1 / 2 / 2;
    position: relative;

    img {
      position: absolute;
      right: 0;
      top: 0;
    }

    svg {
      width: 101.259px;
      height: 128.942px;
      flex-shrink: 0;
      margin-bottom: 16px;
    }

    p {
      color: #fff;
      text-align: center;
      font-family: SF Pro Display;
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
    }

    span {
      color: rgba(255, 255, 255, 0.6);
      text-align: center;
      font-family: SF Pro Display;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }

    @media (max-width: 768px) {
      svg {
        transform: scale(1.2);
      }

      p {
        color: #fff;
        text-align: center;
        font-family: SF Pro Display;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
      }

      span {
        color: rgba(255, 255, 255, 0.6);
        text-align: center;
        font-family: SF Pro Display;
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        opacity: 0.6;
      }
    }
  }

  .div2 {
    grid-area: 1 / 2 / 2 / 4;
    position: relative !important;

    img {
      position: absolute;
      right: 90px;
      top: 85px;
      transform: scale(2.5) rotate(180deg);
    }

    p {
      color: #fff;
      font-family: SF Pro Display;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      opacity: 0.6;
    }

    button {
      margin-top: 30px;
      border-width: 1.8px;
      padding: 4px 34px;
      font-size: 14px;
      letter-spacing: -0.265px;
      border-radius: 10px;
    }

    h3 {
      color: #fff;
      font-family: SF Pro Display;
      font-size: 26px;
      font-style: normal;
      font-weight: 700;
    }

    svg {
      margin: 28px 0px;
      transform: scale(1.12);
    }

    @media (max-width: 768px) {
      h3 {
        color: #fff;
        font-family: SF Pro Display;
        font-size: 26px;
        font-style: normal;
        font-weight: 700;
        line-height: 37.626px; /* 144.715% */
      }

      p {
        color: #fff;
        font-family: SF Pro Display;
        font-size: 16px !important;
        font-style: normal;
        font-weight: 600;
        line-height: 37.626px; /* 235.161% */
        opacity: 0.6;
      }

      button {
        padding: 4px 22px;
      }
    }
  }

  .div3 {
    grid-area: 2 / 1 / 3 / 3;
    overflow: hidden;

    img {
      mix-blend-mode: screen;
      opacity: 0.7;
      width: 100%;
      height: 300px;
      object-fit: cover;
    }
  }

  .div4 {
    grid-area: 2 / 3 / 3 / 4;
    position: relative !important;

    img {
      position: absolute;
      left: 20px;
      top: 35px;
      transform: scale(1.7);
    }

    svg {
      margin-bottom: 8px;
      width: 119.152px;
      height: 52.07px;
    }

    h3,
    p {
      color: #fff;
      text-align: left !important;
    }

    h3 {
      color: #fff;
      font-family: SF Pro Display;
      font-size: 26px;
      font-style: normal;
      font-weight: 700;
      margin-bottom: 14px;
    }

    p {
      color: rgba(255, 255, 255, 0.56);
      font-family: SF Pro Display;
      font-size: 15px;
      font-style: normal;
      margin-bottom: 4px;
    }

    .color {
      color: #d4befd !important;
      opacity: 1 !important;
    }

    @media (max-width: 768px) {
      svg {
        max-width: 100px;
      }

      h3 {
        color: #fff;
        font-family: SF Pro Display;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        margin-bottom: 8px;
      }

      p {
        color: rgba(255, 255, 255, 0.8);
        font-family: SF Pro Display;
        font-size: 14px !important;
        font-style: normal;
        font-weight: 600;
      }

      .color {
        color: #d4befd;
        font-family: SF Pro Display;
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
      }
    }
  }

  .div5 {
    grid-area: 1 / 4 / 3 / 5;
    min-width: 400px;
    position: relative !important;

    > img {
      position: absolute;
      right: 130px;
      top: 150px;
      transform: scale(3) rotate(90deg);
      /* z-index: 999999; */
    }

    > div {
      width: 100%;
      padding: 30px;

      > div {
        > div {
          /* margin-bottom: 30px; */

          h3 {
            color: #fff;
            font-family: SF Pro Display;
            font-size: 26px;
            font-style: normal;
            font-weight: 700;
            line-height: 37.626px; /* 144.715% */
            margin-bottom: 24px;

            strong {
              color: #fff;
            }
          }

          label {
            color: rgb(255 255 255 / 60%);
            font-family: SF Pro Display;
            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            /* line-height: 37.626px; */
          }
        }
      }
    }
  }

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);

    padding-top: 80px;

    .div1 {
      grid-area: 1 / 1 / 2 / 2;

      svg {
        width: 80px;
        margin-top: 30px;
      }

      span {
        margin-bottom: 30px;
      }
    }
    .div2 {
      grid-area: 1 / 2 / 2 / 3;

      p {
        margin-bottom: 16px;
        font-size: 14px;
      }

      h3 {
        margin-top: 30px;
        font-size: 24px;
      }

      svg {
        margin: 24px 0px;
      }
    }
    .div3 {
      grid-area: 2 / 1 / 3 / 2;

      h3 {
        margin-top: 30px;
        margin-bottom: 4px;
      }

      p {
        color: #fff;
        opacity: 0.56;
        font-size: 14px;
      }

      .progressContainer {
        margin-bottom: 30px;

        h1 {
          font-size: 20px;
          color: #fff;
          margin-bottom: 4px;
        }
      }
    }
    .div4 {
      grid-area: 2 / 2 / 3 / 3;

      svg {
        margin-bottom: 8px;
        width: 130px;
      }

      h3 {
        font-size: 20px;
      }

      p {
        font-size: 14px;
        opacity: 0.3;
        line-height: 18px;
      }
    }
    .div5 {
      display: none;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 0.5fr);
    grid-template-rows: repeat(4, 0fr);

    padding-bottom: unset;

    .div1 {
      grid-area: 1 / 1 / 2 / 2;
    }
    .div2 {
      grid-area: 2 / 1 / 3 / 3;

      button {
        margin-top: 0px;
        margin-bottom: 28px;
      }
    }
    .div3 {
      grid-area: 4 / 1 / 5 / 3;
    }
    .div4 {
      grid-area: 1 / 2 / 2 / 3;
    }
    .div5 {
      display: block;
      grid-area: 3 / 1 / 4 / 3;
      min-width: unset;

      > div > div > div:nth-child(1) {
        display: flex;
        flex-direction: column;
        align-items: center;

        label {
          margin-left: -68px;
          font-family: SF Pro Display;
          font-size: 16px;
          font-style: normal;
          font-weight: 600;
        }

        h3 {
          color: #fff;
          font-family: SF Pro Display;
          font-size: 26px;
          font-style: normal;
          font-weight: 700;
        }
      }
    }
  }
`;
